/**
 * Product Entropy Screen (Step 6)
 * Tabbed layout: Demand, Supply, Delivery, MarketFit, Development (if product_state=development).
 * 5 slider questions per tab, live entropy meter, overall Product Entropy Score.
 */
import React, { useState, useEffect } from 'react';
import { Tabs, Slider, Card, Button, Typography, Spin, message } from 'antd';
import {
  getProductDomainQuestions,
  submitProductDomainAnswers,
  calculateProductDomainSession,
  getProductDomainSession,
  getProductDomainEntropyMap,
} from '../../services/productDomainSessionApi';
import type { ProductDomainQuestion } from '../../services/productDomainSessionApi';

const { Text } = Typography;

interface ProductEntropyScreenProps {
  sessionId: string;
  onNext: () => void;
}

const SLIDER_SCORES = [1, 2, 4, 6, 8, 10] as const;

function groupByDomain(questions: ProductDomainQuestion[]): Record<string, ProductDomainQuestion[]> {
  const by: Record<string, ProductDomainQuestion[]> = {};
  for (const q of questions) {
    const d = q.domain || 'Other';
    if (!by[d]) by[d] = [];
    by[d].push(q);
  }
  return by;
}

export const ProductEntropyScreen: React.FC<ProductEntropyScreenProps> = ({
  sessionId,
  onNext,
}) => {
  const [questions, setQuestions] = useState<ProductDomainQuestion[]>([]);
  const [session, setSession] = useState<{ product_state?: string } | null>(null);
  const [entropyMap, setEntropyMap] = useState<{ subdomains: Record<string, { entropy_score: number }>; aggregate_entropy_score: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [answers, setAnswers] = useState<Record<string, number>>({});

  useEffect(() => {
    const load = async () => {
      const [qList, s, map] = await Promise.all([
        getProductDomainQuestions(sessionId, 'entropy_assessment'),
        getProductDomainSession(sessionId),
        getProductDomainEntropyMap(sessionId),
      ]);
      setQuestions(qList);
      setSession(s);
      setEntropyMap(map);
      const initial: Record<string, number> = {};
      for (const q of qList) {
        initial[q.question_id] = q.current_answer?.score ?? 4;
      }
      setAnswers(initial);
      setLoading(false);
    };
    load();
  }, [sessionId]);

  const byDomain = groupByDomain(questions);
  const tabOrder = ['Demand', 'Supply', 'Delivery', 'MarketFit'];
  if (session?.product_state === 'development') tabOrder.push('Development');

  const handleScoreChange = (questionId: string, score: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: score }));
  };

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.question_id] != null);

  const handleSaveAndNext = async () => {
    if (!allAnswered) {
      message.warning('Please answer all questions');
      return;
    }
    setSaving(true);
    const answerList = questions.map((q) => ({
      question_id: q.question_id,
      score: answers[q.question_id] ?? 4,
    }));
    const submitOk = await submitProductDomainAnswers(sessionId, 'current', answerList);
    if (!submitOk?.ok) {
      setSaving(false);
      message.error('Failed to save answers');
      return;
    }
    await calculateProductDomainSession(sessionId);
    const map = await getProductDomainEntropyMap(sessionId);
    if (map) setEntropyMap(map);
    setSaving(false);
    onNext();
  };

  if (loading) return <Spin tip="Loading questions..."><div style={{ minHeight: 120 }} /></Spin>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Rate each dimension. <strong>1</strong> = tightly coordinated, low entropy (well-optimized).{' '}
        <strong>10</strong> = chaotic, high entropy (significant improvement needed). Lower is better.
      </Typography.Paragraph>
      <Tabs
        defaultActiveKey={tabOrder[0]}
        items={tabOrder.map((domain) => ({
          key: domain,
          label: domain,
          children: (
            <Card>
              {(byDomain[domain] || []).map((q) => (
                <div key={q.question_id} style={{ marginBottom: 24 }}>
                  <Text strong>{q.text}</Text>
                  <Slider
                    min={1}
                    max={10}
                    step={null}
                    reverse
                    marks={Object.fromEntries(
                      SLIDER_SCORES.map((s) => [
                        s,
                        {
                          label: s === 1 ? '1 (Best)' : s === 10 ? '10 (Worst)' : String(s),
                          style: { whiteSpace: 'nowrap' as const },
                        },
                      ])
                    )}
                    value={answers[q.question_id] ?? 4}
                    onChange={(v) => handleScoreChange(q.question_id, v)}
                    {...{ startPoint: 10 }}
                  />
                </div>
              ))}
            </Card>
          ),
        }))}
      />

      <Card title="Entropy Summary" style={{ marginTop: 24 }}>
        {entropyMap?.subdomains && (
          <div style={{ marginBottom: 16 }}>
            {Object.entries(entropyMap.subdomains).map(([sub, data]) => (
              <div key={sub}>
                <Text type="secondary">{sub}</Text>: <Text strong>{data.entropy_score.toFixed(1)}</Text>
              </div>
            ))}
          </div>
        )}
        <div>
          <Text type="secondary">Overall Product Entropy Score</Text>
          <div><Text strong style={{ fontSize: 24 }}>{entropyMap?.aggregate_entropy_score?.toFixed(1) ?? '—'}</Text></div>
        </div>
      </Card>

      <Button
        type="primary"
        onClick={handleSaveAndNext}
        loading={saving}
        disabled={!allAnswered}
        style={{ marginTop: 24 }}
      >
        Save & Next
      </Button>
    </div>
  );
};
